import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Menu, Settings, TrendingUp, TrendingDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Select, Modal, Radio } from 'antd';
import { NAV_ITEMS } from '@utils/navConfig';
import { useNavbarInstrument } from '@hooks/useNavbarInstrument';
import NavItem from './NavItem';
import ProfileMenu from './ProfileMenu';
import MobileDrawer from './MobileDrawer';
import useMediaQuery from '@hooks/useMediaQuery';
import './TopNavbar.scss';
import logo from '@assets/logo/fnotrader.png';

const QUIKTRADE_URL = import.meta.env.VITE_QUIKTRADE_URL

const TopNavbar = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [configOpen, setConfigOpen] = useState(false);
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    
    const {
        instruments,
        selectedInstrument,
        spotPrice,
        spotChangePercent,
        futPrice,
        futChangePercent,
        priceType,
        handleInstrumentChange,
        handlePriceTypeChange,
    } = useNavbarInstrument();

    useEffect(() => { if (isDesktop) setDrawerOpen(false); }, [isDesktop]);

    const activePrice = priceType === 'Spot' ? spotPrice : futPrice;
    const activeChangePercent = priceType === 'Spot' ? spotChangePercent : futChangePercent;

    return (
        <>
            <header className="top-navbar">

                {!isDesktop && (
                    <button className="nav-hamburger" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
                        <Menu size={18} />
                    </button>
                )}

                <Link to="/pages/technical-analysis" className="top-navbar__logo">
                    <img src={logo} alt="logo" />
                    <span>Discover</span>
                </Link>

                {isDesktop && (
                    <nav className="top-navbar__nav">
                        {NAV_ITEMS.map(item => <NavItem key={item.key} item={item} />)}
                    </nav>
                )}

                <div className="top-navbar__right">
                    {isDesktop && (
                        <>
                            {/* Price Ticker Pill */}
                            <button 
                                className="top-navbar__price-pill" 
                                onClick={() => setConfigOpen(true)}
                                title="Click to configure instrument and price type"
                            >
                                <span className="ticker-symbol">{selectedInstrument}</span>
                                <span className="ticker-label">{priceType === 'Spot' ? 'Spot' : 'Fut'}</span>
                                <span className={`ticker-value ${activeChangePercent >= 0 ? 'ticker-value--up' : 'ticker-value--down'}`}>
                                    {activePrice != null ? Number(activePrice).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                                    {activePrice != null && activeChangePercent != null && (
                                        <span className="ticker-pct">
                                            {activeChangePercent >= 0 ? (
                                                <ArrowUp size={12} className="ticker-trend-icon" />
                                            ) : (
                                                <ArrowDown size={12} className="ticker-trend-icon" />
                                            )}
                                            {Math.abs(Number(activeChangePercent)).toFixed(2)}%
                                        </span>
                                    )}
                                </span>
                            </button>

                            <Link to="/account/broker-login" className="nav-btn nav-btn--outline">
                                <Plus size={13} strokeWidth={2.5} /> Add Broker
                            </Link>
                            <a href={QUIKTRADE_URL} target="_blank" rel="noopener noreferrer" className="nav-btn nav-btn--quiktrade">
                                <span className="quik">Quik</span><span className="trade">Trade</span>
                            </a>
                        </>
                    )}
                    <ProfileMenu />
                </div>

            </header>

            <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

            {/* Price Ticker Configuration Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: 600 }}>
                        <Settings size={18} style={{ color: '#2563eb' }} />
                        <span>Configure Price Ticker</span>
                    </div>
                }
                open={configOpen}
                onCancel={() => setConfigOpen(false)}
                footer={null}
                width={360}
                centered
                className="ticker-config-modal"
            >
                <div style={{ padding: '8px 0 16px' }}>
                    <div className="config-form-item" style={{ marginBottom: '18px' }}>
                        <label className="config-form-label" style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '8px', letterSpacing: '0.5px' }}>
                            FNO INSTRUMENT
                        </label>
                        <Select
                            showSearch
                            style={{ width: '100%' }}
                            placeholder="Select Instrument"
                            optionFilterProp="children"
                            value={selectedInstrument}
                            onChange={handleInstrumentChange}
                            filterOption={(input, option) =>
                                (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={instruments.map((name) => ({ label: name, value: name }))}
                            size="large"
                        />
                    </div>

                    <div className="config-form-item">
                        <label className="config-form-label" style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '8px', letterSpacing: '0.5px' }}>
                            PRICE TYPE FEED
                        </label>
                        <Radio.Group
                            value={priceType}
                            onChange={(e) => handlePriceTypeChange(e.target.value)}
                            buttonStyle="solid"
                            style={{ display: 'flex', width: '100%' }}
                            size="large"
                        >
                            <Radio.Button value="Spot" style={{ flex: 1, textAlign: 'center' }}>Spot Price</Radio.Button>
                            <Radio.Button value="Future" style={{ flex: 1, textAlign: 'center' }}>Future Price</Radio.Button>
                        </Radio.Group>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default TopNavbar;